import { useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useCMSPageField } from '@/cms/fields'
import { Custom500 } from '@/pages/500'
import { Basket } from '@/scopes/checkout/components/Basket'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutFooter } from '@/scopes/checkout/components/CheckoutFooter'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import { CheckoutHeading, CheckoutHeadingTitle } from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutPreviousStepHeader } from '@/scopes/checkout/components/CheckoutPreviousStepHeader'
import backButtonStyles from '@/scopes/checkout/components/CheckoutPreviousStepHeader/CheckoutPreviousStepHeader.module.css'
import {
  CheckoutProgress,
  CheckoutProgressForMandatoryPaymentPage,
} from '@/scopes/checkout/components/CheckoutProgress'
import { EngineError } from '@/scopes/checkout/components/EngineError/EngineError'
import { Faq } from '@/scopes/checkout/components/Faq/Faq'
import { Content, Loader, MainContent, PageLayout, PrimaryContent, SideBarContent } from '@/scopes/checkout/components/Layout'
import { RebookingPayment } from '@/scopes/checkout/components/RebookingPayment/RebookingPayment'
import { TrustIcons } from '@/scopes/checkout/components/TrustIcons/TrustIcons'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { CheckoutOptimizelyFlagsProvider } from '@/scopes/checkout/domain/CheckoutOptimizelyFlagsProvider'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import { shouldShowTrustIcons } from '@/scopes/checkout/domain/policies/brand'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { PaymentBackButton } from '@/scopes/payment/components/PaymentBackButton'
import { PaymentReactContext, PaymentStepProvider } from '@/scopes/payment/domain/PaymentContext'
import { PaymentActionsDependenciesProvider } from '@/scopes/payment/infrastructure/dependencies'
import { useSendCheckoutEvent } from '../domain/analytics/useSendCheckoutEvent'

const queryClient = new QueryClient()

function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const intl = useIntl()
  const brandName = useConfiguration('brandName')
  useSendCheckoutEvent('checkout_mandatory_payment_reached')
  const showTrustIcon = shouldShowTrustIcons(brandName)
  const { showMandatoryPaymentPage } = useOptimizelyFlagContext()

  if (data?.error && data.error.hasError) {
    return (
      <PageLayout>
        {!data.error.errorCode && data.error.redirectUrl
          ? (
              <CheckoutError />
            )
          : (
              <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
                <CheckoutHeader />
                <main id="main-content" tabIndex={-1}>
                  <EngineError error={data.error} />
                </main>
              </NewRelicErrorBoundary>
            )}
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
        <CheckoutHeader showBackButton={false}>
          <PaymentBackButton className={backButtonStyles.backButton}>
            <CheckoutPreviousStepHeader />
          </PaymentBackButton>
        </CheckoutHeader>
        <MainContent>
          {isLoading
            ? (
                <Loader
                  text={intl.formatMessage({
                    id: 'checkout_steps_mandatory_payment_validation_loading_text',
                  })}
                />
              )
            : (
                <>
                  {showMandatoryPaymentPage ? <CheckoutProgressForMandatoryPaymentPage /> : <CheckoutProgress />}
                  <CheckoutHeading>
                    <CheckoutHeadingTitle>
                      {intl.formatMessage({ id: 'checkout_steps_step_mandatory_payment_title', defaultMessage: 'Payment' })}
                    </CheckoutHeadingTitle>
                  </CheckoutHeading>
                  <Content>
                    <PrimaryContent>
                      <RebookingPayment />
                    </PrimaryContent>
                    <SideBarContent>
                      <Basket />
                      <div>
                        <Faq />
                        {showTrustIcon && <TrustIcons />}
                      </div>
                    </SideBarContent>
                  </Content>
                </>
              )}
        </MainContent>
        {!isLoading && <CheckoutFooter />}
      </NewRelicErrorBoundary>
    </PageLayout>
  )
}

export default function MandatoryPayment() {
  const contextItemId = useCMSPageField('contextItemId')
  const sid = useCheckoutSid()
  const { isMobile } = useBreakpoints()
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
  }), [contextItemId])
  const paymentContextValue = React.useMemo(() => ({
    contextItemId,
    groupUserId: null,
    scope: 'MandatoryPayment' as const,
    bookingNumber: sid ?? '',
  }), [contextItemId, sid])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <CheckoutOptimizelyFlagsProvider>
          <PaymentReactContext value={paymentContextValue}>
            <PaymentStepProvider isWizard={isMobile}>
              <PaymentActionsDependenciesProvider>
                <MetaTitle titleTranslationTag="checkout_steps_mandatory_payment_meta_title" />
                <Page />
                <DevTools />
              </PaymentActionsDependenciesProvider>
            </PaymentStepProvider>
          </PaymentReactContext>
        </CheckoutOptimizelyFlagsProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
